import { transcodingStatusStateCssClass } from 'app/helpers/streamWorkState'

function transcoderStatusLabelFromStream (stream) {
  const raw = stream?.status?.statusLabel ?? stream?.statusLabel
  if (raw == null || String(raw).trim() === '') return null
  return String(raw).trim()
}

function transcoderStatusStateFromStream (stream) {
  const raw = stream?.status?.statusState ?? stream?.statusState
  if (raw == null || String(raw).trim() === '') return null
  return String(raw).trim()
}

function templateFieldKey (field, index) {
  if (!field || typeof field !== 'object') return String(index)
  const byId = field.id ?? field.fieldId ?? field.externalId
  if (byId != null && String(byId).trim() !== '') return String(byId).trim()
  const byName = field.name ?? field.fieldName ?? field.key
  if (byName != null && String(byName).trim() !== '') return String(byName).trim()
  return String(index)
}

/**
 * Builds one stream-creation template field for POST …/streams (templateExternalId + fields).
 *
 * @param {Object} field template field definition from API
 * @param {*} rawValue value from formData.templateFieldValues[field._key]
 * @returns {{ name: string, value: * }}
 */
function buildCreateStreamTemplateField (field, rawValue) {
  const nameRaw = field?.name ?? field?.fieldName ?? field?.key ?? templateFieldKey(field, 0)
  const name = nameRaw == null ? '' : String(nameRaw)
  const value = rawValue == null ? '' : String(rawValue)
  return { name, value }
}

/* @ngInject */
export default function AddTranscoderModalCtrl (
  $scope,
  $http,
  $q,
  $uibModalInstance,
  alertLiteSvc,
  SetrixServer,
  InappSettings,
  networkId,
  setrixServersPrefetch,
  assignedStreamBindings
) {
  $scope.transcodingStatusStateCssClass = transcodingStatusStateCssClass

  $scope.formData = {
    setrixServerId: null,
    action: 'EXISTING',
    transcoderId: null,
    templateId: null,
    templateFieldValues: {}
  }

  $scope.setrixServers = []
  $scope.transcoders = []
  $scope.templates = []
  $scope.templateDetails = null
  $scope.templateFields = []
  $scope.templateFieldsLoading = false
  $scope.submitting = false

  function normalizeOptions (items = [], defaultLabel = 'Item', getId) {
    return items.map((item, index) => ({
      id: (typeof getId === 'function')
        ? getId(item, index)
        : (item.id || item.serverId || item.transcoderId || item.uuid || index + 1),
      name: item.name || item.serverName || item.transcoderName || item.label || `${defaultLabel} ${index + 1}`,
      networkId: item.networkId ?? null
    }))
  }

  const multiorgEnabled = !!InappSettings?._current?.multiorgEnabled
  function getSelectedServerNetworkId (serverId) {
    if (!multiorgEnabled) return null
    if (serverId == null) return null
    const server = ($scope.setrixServers || []).find((s) => String(s?.id) === String(serverId))
    return server?.networkId ?? null
  }
  function buildParams (serverId) {
    const nid = getSelectedServerNetworkId(serverId)
    return (multiorgEnabled && nid != null) ? { networkId: nid } : {}
  }

  function unwrapPayload (data) {
    if (!data) return data
    if (typeof data === 'object' && data.status && data.payload) return data.payload
    if (typeof data === 'object' && data.payload) return data.payload
    return data
  }

  function pickList (payload) {
    if (!payload) return []
    if (Array.isArray(payload)) return payload
    return (
      payload.streams ||
      payload.items ||
      payload.data ||
      payload.results ||
      payload.content ||
      []
    )
  }

  let transcodersRequestSeq = 0
  const transcodersPageSize = 1000

  const assignedBindings = Array.isArray(assignedStreamBindings) ? assignedStreamBindings : []

  function buildAssignedStreamIdSetForServer (serverId) {
    const ids = new Set()
    if (serverId == null) return ids
    const serverKey = String(serverId)
    assignedBindings.forEach((b) => {
      if (b?.streamId == null) return
      const bServer = b.serverId
      if (bServer == null || String(bServer) === serverKey) {
        ids.add(String(b.streamId))
      }
    })
    return ids
  }

  function appendTranscodersPage (page) {
    const paging = $scope.transcodersPaging
    if (!paging?.serverId) return Promise.resolve()
    if (paging.loading) return Promise.resolve()
    if (paging.requestSeq !== transcodersRequestSeq) return Promise.resolve()

    paging.loading = true
    return $http.get(`/nora/api/content-delivery/servers/${paging.serverId}/streams`, {
      params: { ...buildParams(paging.serverId), page, size: transcodersPageSize }
    })
      .then((response) => unwrapPayload(response?.data))
      .then((payload) => {
        if (paging.requestSeq !== transcodersRequestSeq) return

        paging.page = Number(payload?.number ?? page) || 0
        paging.totalPages = Number(payload?.totalPages || 0) || 0

        const streams = pickList(payload)
        const baseIndex = page * transcodersPageSize
        const newItems = streams.map((stream, index) => ({
          id: (
            stream?.externalId ||
            stream?.transcoderId ||
            stream?.id ||
            stream?.uuid ||
            baseIndex + index + 1
          ),
          name: stream?.name || stream?.transcoderName || `Transcoder ${baseIndex + index + 1}`,
          statusLabel: transcoderStatusLabelFromStream(stream),
          statusState: transcoderStatusStateFromStream(stream),
          active: !!(stream?.status?.active ?? stream?.active)
        }))
        const assignedIds = buildAssignedStreamIdSetForServer(paging.serverId)
        const filteredItems = newItems.filter((item) => !assignedIds.has(String(item.id)))

        $scope.transcoders = $scope.transcoders.concat(filteredItems)
      })
      .catch((rejection) => {
        if (paging.requestSeq !== transcodersRequestSeq) return
        alertLiteSvc.alertError(rejection?.statusText || 'Failed to load transcoders')
      })
      .finally(() => {
        if (paging.requestSeq !== transcodersRequestSeq) return
        paging.loading = false
      })
  }

  function loadTranscoders (serverId) {
    $scope.transcoders = []
    $scope.formData.transcoderId = null
    if (!serverId) return

    const requestSeq = ++transcodersRequestSeq

    $scope.transcodersPaging = {
      serverId,
      page: -1,
      totalPages: 0,
      loading: false,
      requestSeq
    }
    appendTranscodersPage(0).then(() => {
      if ($scope.transcodersPaging?.requestSeq !== requestSeq) return
      if ($scope.transcoders.length === 1 && $scope.transcodersPaging.totalPages <= 1) {
        $scope.formData.transcoderId = $scope.transcoders[0].id
      }
    })
  }

  $scope.onTranscodersScroll = function (event) {
    const paging = $scope.transcodersPaging
    if (!paging?.serverId || paging.loading) return

    const { page, totalPages, serverId } = paging
    if (!Number.isFinite(totalPages) || totalPages <= 0) return
    if (page >= totalPages - 1) return

    const el = event?.currentTarget
    if (!el) return

    if (el.scrollTop >= (el.scrollHeight - el.clientHeight) * 0.6) {
      if (serverId !== $scope.formData?.setrixServerId) return
      appendTranscodersPage(page + 1)
    }
  }

  function loadTemplates (serverId) {
    $scope.templates = []
    if (!serverId) return
    SetrixServer.loadStreamTemplates(serverId, getSelectedServerNetworkId(serverId), multiorgEnabled)
      .then((templates) => {
        $scope.templates = normalizeOptions(templates, 'Template', (template, index) => (
          template.externalId || template.id || template.uuid || index + 1
        ))
        if ($scope.templates.length === 1) {
          $scope.formData.templateId = $scope.templates[0].id
        }
      })
  }

  let templateFieldsRequestSeq = 0
  function loadTemplateFields (serverId, templateId) {
    $scope.templateDetails = null
    $scope.templateFields = []
    const prevTemplateFieldValues = $scope.formData?.templateFieldValues || {}
    $scope.formData.templateFieldValues = {}
    if (!serverId || !templateId) return

    const requestSeq = ++templateFieldsRequestSeq
    $scope.templateFieldsLoading = true

    $http.get(
      `/nora/api/content-delivery/servers/${serverId}/streams/templates/${templateId}`,
      { params: buildParams(serverId) }
    )
      .then((response) => unwrapPayload(response?.data))
      .then((data) => {
        if (requestSeq !== templateFieldsRequestSeq) return
        const payload = unwrapPayload(data)
        $scope.templateDetails = payload || null
        const rawFields = payload?.fields || payload?.parameters || payload?.params || payload?.content || []
        const fields = Array.isArray(rawFields) ? rawFields : []
        $scope.templateFields = fields.map((f, index) => ({ ...f, _key: templateFieldKey(f, index) }))
        const values = {}
        $scope.templateFields.forEach((field) => {
          const key = field?._key
          if (key == null || String(key).trim() === '') return
          const prev = prevTemplateFieldValues?.[key]
          const def = field.defaultValue != null ? String(field.defaultValue) : ''
          values[key] = prev != null && prev !== '' ? prev : def
        })
        $scope.formData.templateFieldValues = values
      })
      .catch((rejection) => {
        if (requestSeq !== templateFieldsRequestSeq) return
        alertLiteSvc.alertError(rejection?.statusText || 'Failed to load template fields')
      })
      .finally(() => {
        if (requestSeq !== templateFieldsRequestSeq) return
        $scope.templateFieldsLoading = false
      })
  }

  // $uibModal resolve unwraps promises — injectable may be the $http response object, not a thenable.
  $q.when(setrixServersPrefetch != null ? setrixServersPrefetch : SetrixServer.loadAll(null, multiorgEnabled))
    .then((response) => {
      if (Array.isArray(response)) return response
      return unwrapPayload(response?.data) || []
    })
    .then((servers) => {
      $scope.setrixServers = normalizeOptions(servers, 'Server')
      if ($scope.setrixServers.length === 1) {
        $scope.formData.setrixServerId = $scope.setrixServers[0].id
      }
    })
    .catch((rejection) => {
      alertLiteSvc.alertError(rejection?.statusText || 'Failed to load Setrix servers')
    })

  $scope.$watchGroup(['formData.action', 'formData.setrixServerId'], ([action, serverId], [prevAction, prevServerId]) => {
    const serverChanged = serverId !== prevServerId
    if (serverChanged) {
      if (action === 'CREATE') {
        $scope.formData.templateId = null
        $scope.formData.templateFieldValues = {}
      } else {
        $scope.formData.transcoderId = null
      }
    }

    if (action !== prevAction) {
      if (action === 'CREATE') {
        $scope.formData.transcoderId = null
        $scope.formData.templateId = null
        $scope.formData.templateFieldValues = {}
      } else {
        $scope.formData.templateId = null
      }
    }
    if (action === 'CREATE') {
      loadTemplates(serverId)
    } else {
      $scope.templates = []
      loadTranscoders(serverId)
    }
  })

  $scope.$watchGroup(['formData.action', 'formData.setrixServerId', 'formData.templateId'], ([action, serverId, templateId]) => {
    if (action !== 'CREATE') {
      templateFieldsRequestSeq++
      $scope.templateDetails = null
      $scope.templateFields = []
      $scope.templateFieldsLoading = false
      return
    }
    loadTemplateFields(serverId, templateId)
  })

  $scope.cancel = function () {
    $uibModalInstance.dismiss('cancel')
  }

  $scope.submit = function () {
    if ($scope.submitting) return

    const { setrixServerId, transcoderId, templateId, action, templateFieldValues } = $scope.formData

    const server = $scope.setrixServers.find(s => s.id === setrixServerId)
    const transcoder = $scope.transcoders.find(t => t.id === transcoderId)
    const template = $scope.templates.find(t => t.id === templateId)

    if (action === 'CREATE') {
      $scope.submitting = true
      const fields = $scope.templateFields
        .filter((field) => field?._key != null && String(field._key).trim() !== '')
        .map((field) => buildCreateStreamTemplateField(field, templateFieldValues[field._key]))
        .filter((f) => f?.name != null && String(f.name).trim() !== '')
      const body = {
        templateExternalId: String(templateId),
        fields
      }

      SetrixServer.createStream(setrixServerId, body, getSelectedServerNetworkId(setrixServerId), multiorgEnabled)
        .then((response) => {
          let data = unwrapPayload(response?.data)
          data = unwrapPayload(data)
          const stream = data?.stream ?? data
          const newStreamId = (
            stream?.externalId ??
            stream?.transcoderId ??
            stream?.id ??
            stream?.uuid ??
            null
          )
          const newStreamName = stream?.name ?? stream?.transcoderName ?? template?.name ?? null

          if (!newStreamId) {
            alertLiteSvc.alertError('Failed to create transcoder')
            return
          }

          alertLiteSvc.alert('Transcoder created successfully', 'success')
          $uibModalInstance.close({
            action,
            setrixServerId,
            setrixServerName: server?.name || null,
            setrixServerNetworkId: server?.networkId ?? null,
            transcoderId: newStreamId,
            transcoderName: newStreamName,
            templateId: null,
            templateName: null,
            templateFieldValues: {}
          })
        })
        .catch((rejection) => {
          alertLiteSvc.alertError('Failed to create transcoder')
        })
        .finally(() => {
          $scope.submitting = false
        })
      return
    }

    $uibModalInstance.close({
      action,
      setrixServerId,
      setrixServerName: server?.name || null,
      setrixServerNetworkId: server?.networkId ?? null,
      transcoderId: transcoderId || null,
      transcoderName: transcoder?.name || null,
      templateId: templateId || null,
      templateName: template?.name || null,
      templateFieldValues: angular.copy(templateFieldValues || {})
    })
  }
}
